import { createContext, useCallback, useContext, useMemo } from "react";

import { useGetLayerFeatureTotalsQuery, useGetProjectQuery, useGetVersionQuery } from "fond/api";
import { dismissBaseDataTask } from "fond/redux/baseDataTasks";
import { area } from "fond/turf";
import { formatArea } from "fond/utils/area";
import { useAppDispatch } from "fond/utils/hooks";

import { useDeleteBaseData } from "./flyout/useDeleteBaseData";
import { BASE_DATA_LAYER_IDS, BASE_DATA_LAYER_KEYS, BaseDataLayerCounts, BaseDataLayerKey, GenerationKey } from "./layers";
import { BaseDataModelState, getBaseDataModel } from "./model";
import { useBaseDataGeneration } from "./useBaseDataGeneration";

type UseBaseDataModelProps = {
  projectId: string | undefined;
  versionId: string;
  readOnly: boolean;
  /** Whether to fetch the feature counts and boundary area shown only by the Input Data panel. */
  enabled?: boolean;
};

export type BaseDataModelActions = {
  /** Run a generation workflow. */
  run(key: GenerationKey): void;
  /** Cancel the running generation. */
  cancel(): void;
  /** Delete every planner layer of a base data row, or the boundary. */
  deleteData(key: BaseDataLayerKey): Promise<void>;
  /** Clear the task started from a base data row's flyout. */
  dismissTask(key: BaseDataLayerKey): void;
};

export type BaseDataModel = BaseDataModelState & { actions: BaseDataModelActions };

/** Return the state of the Input Data panel's base data rows and the actions that change them. */
export const useBaseDataModel = ({ projectId, versionId, readOnly, enabled = true }: UseBaseDataModelProps): BaseDataModel => {
  const dispatch = useAppDispatch();
  const generation = useBaseDataGeneration({ projectId, versionId, readOnly });
  const { currentData: featureTotals } = useGetLayerFeatureTotalsQuery({ versionId }, { skip: !enabled || !versionId });
  const { data: project } = useGetProjectQuery(projectId ?? "", { skip: !enabled || !projectId });
  const { data: version } = useGetVersionQuery(versionId, { skip: !enabled || !versionId });
  const deleteData = useDeleteBaseData();

  const counts = useMemo(
    () =>
      Object.fromEntries(
        BASE_DATA_LAYER_KEYS.flatMap((key) => BASE_DATA_LAYER_IDS[key]).map((layerId) => [layerId, featureTotals?.[layerId]?.count])
      ) as BaseDataLayerCounts,
    [featureTotals]
  );

  // Falls back to a caption without the area while the project loads.
  const uploadedArea = version?.UploadedArea;
  const systemOfMeasurement = project?.SystemOfMeasurement;
  const boundaryArea = useMemo(() => {
    if (!uploadedArea) return null;
    return systemOfMeasurement ? formatArea(area(uploadedArea), systemOfMeasurement) : "Boundary added";
  }, [uploadedArea, systemOfMeasurement]);

  const { present, hasData, missing, staleness, gating, running, failed, progressSteps, ownedTasks, run, cancel } = generation;
  const state = useMemo(
    () =>
      getBaseDataModel({
        present,
        hasData,
        missing,
        staleness,
        gating,
        running,
        failed,
        progressSteps,
        ownedTasks,
        counts,
        boundaryArea,
        readOnly,
      }),
    [present, hasData, missing, staleness, gating, running, failed, progressSteps, ownedTasks, counts, boundaryArea, readOnly]
  );

  const dismissTask = useCallback((key: BaseDataLayerKey) => dispatch(dismissBaseDataTask(key)), [dispatch]);
  const actions = useMemo(() => ({ run, cancel, deleteData, dismissTask }), [run, cancel, deleteData, dismissTask]);

  return useMemo(() => ({ ...state, actions }), [state, actions]);
};

/** The Input Data panel's base data model, for the components inside the panel and its flyouts. */
export const BaseDataModelContext = createContext<BaseDataModel | null>(null);

/** Return the base data model of the Input Data panel the component is in. */
export const useBaseDataModelContext = (): BaseDataModel => {
  const model = useContext(BaseDataModelContext);
  if (!model) throw new Error("useBaseDataModelContext must be used inside a BaseDataModelContext provider");
  return model;
};
